import React, { useState, useEffect } from "react";
import { fetchFeaturedProducts } from "@/services/teableRecords";
import { trackPromoClick } from "@/utils/analytics";
import { secureLog } from "@/utils/secureLogger";

interface FeaturedProduct {
  id: string;
  fields: {
    LinkAmazon: string;
    Nome: string;
    Immagine: string;
    Prezzo?: string;
    Ordine?: number | string;
    Attivo?: boolean;
  };
}

const FeaturedAmazonProducts = () => {
  const [products, setProducts] = useState<FeaturedProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [headerText, setHeaderText] = useState("Super offerte da non perdere!");

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const prods = await fetchFeaturedProducts();
        const allProducts = prods as unknown as FeaturedProduct[];
        
        // Prima riga per header text
        if (allProducts.length > 0 && allProducts[0].fields.Nome) {
          setHeaderText(allProducts[0].fields.Nome);
        }
        
        // Righe 2-7 (indici 1-6) per le immagini della griglia
        const gridProducts = allProducts
          .slice(1, 7)
          .filter((p: FeaturedProduct) => p.fields.Attivo !== false && p.fields.Immagine && p.fields.LinkAmazon);
        
        setProducts(gridProducts);
      } catch (error) {
        secureLog.error('Featured products load error:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadProducts();
  }, []);

  // Skeleton loading state - reserve exact space to prevent CLS
  if (loading) {
    return (
      <div className="w-full mt-8 mb-6 bg-gradient-to-br from-blue-50 to-purple-50 py-8 relative z-20 min-h-[600px]">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center mb-6">
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-lg">
                  <div className="aspect-square bg-gray-200 animate-pulse" />
                  <div className="p-3 bg-white text-center border-t-2 border-gray-200">
                    <div className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse mx-auto" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Return empty placeholder with min-height when no products to prevent CLS
  if (products.length === 0) {
    return <div className="min-h-[50px]" />;
  }

  const handleProductClick = (product: FeaturedProduct) => {
    trackPromoClick(product.fields.Nome || 'Featured Product', 'featured_amazon');
    window.open(product.fields.LinkAmazon, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="w-full mt-8 mb-6 bg-gradient-to-br from-blue-50 to-purple-50 py-8 relative z-20 min-h-[600px]" aria-labelledby="featured-products-heading">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center mb-6">
          <h2 id="featured-products-heading" className="text-2xl font-bold text-gray-800">
            {headerText}
          </h2>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Griglia 2x3: 2 colonne, 3 righe */}
          <div className="grid grid-cols-2 gap-4">
            {products.slice(0, 6).map((product, index) => (
              <div 
                key={product.id} 
                className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in"
              >
                {/* Immagine cliccabile */}
                <button
                  onClick={() => handleProductClick(product)}
                  className="w-full aspect-square flex items-center justify-center p-4 transition-transform duration-300 hover:scale-105 cursor-pointer"
                  aria-label={`Visualizza ${product.fields.Nome || `Prodotto ${index + 1}`} su Amazon`}
                >
                  <img
                    src={product.fields.Immagine}
                    alt={product.fields.Nome || `Prodotto Amazon ${index + 1}`}
                    className="w-full h-full object-contain"
                    loading="lazy"
                    decoding="async"
                    width="240"
                    height="240"
                  />
                </button>
                
                {/* Bottone Amazon */}
                <div className="p-3 bg-white text-center border-t-2 border-gray-200">
                  <button
                    onClick={() => handleProductClick(product)}
                    className="inline-flex items-center gap-2 bg-[#FF9900] hover:bg-[#FF9900]/90 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                    aria-label={`Acquista ${product.fields.Nome || 'prodotto'} su Amazon`}
                  >
                    🛒 Vedi su Amazon
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <p className="mt-4 text-xs text-gray-500 text-center">
            In qualità di Affiliato Amazon ricevo un guadagno dagli acquisti idonei
          </p>
        </div>
      </div>
    </section>
  );
};

export default FeaturedAmazonProducts;
